import React from 'react';
import * as echarts from 'echarts';
import './StaffManagementModule.less';
import { renderFormattedInt } from '@/utils/renderUtils';

class StaffManagementModule extends React.Component {
  constructor(props) {
    super(props);
    this.gaugeChartRef = React.createRef();
    this.chartInstanceRef = React.createRef();
  }

  /** Redux：fetchCommandCenterStaffAgentsPage；与当前门店一致且无错误时用 totalRow，否则 0 */
  getDisplayStaffTotalRow() {
    const { queryManager, commandCenterStaffAgentsPageObj } = this.props;
    const reduxQuery = commandCenterStaffAgentsPageObj?.query || {};
    const currentStoreId = queryManager?.currentQuery?.store_id;
    const reduxStoreId = reduxQuery?.storeId;
    const queryMatches =
      currentStoreId != null &&
      reduxStoreId != null &&
      String(currentStoreId).trim() === String(reduxStoreId).trim();
    const totalRow = commandCenterStaffAgentsPageObj?.data?.totalRow;
    const err = commandCenterStaffAgentsPageObj?.error;
    const ok =
      queryMatches &&
      err == null &&
      totalRow !== undefined &&
      totalRow !== null &&
      !Number.isNaN(Number(totalRow));
    if (ok) {
      return Number(totalRow) || 0;
    }
    return 0;
  }

  /** 在岗率占位：在岗数暂等于员工总数、离岗为 0 → 有总人数时为 100% */
  getOnDutyRatePercent() {
    const total = this.getDisplayStaffTotalRow();
    if (total <= 0) return 0;
    return 100;
  }
  initChart() {
    if (!this.gaugeChartRef.current) {
      return;
    }

    // 初始化图表
    if (!this.chartInstanceRef.current) {
      const chart = echarts.init(this.gaugeChartRef.current);
      this.chartInstanceRef.current = chart;
    }

    if (this.chartInstanceRef.current && this.gaugeChartRef.current) {
      // 根据容器高度计算自适应字体大小
      const containerHeight = this.gaugeChartRef.current.offsetHeight || 200;
      const detailFontSize = Math.max(14, Math.min(32, containerHeight * 0.12)); // 自适应字体大小，范围14-32
      const titleFontSize = Math.max(10, Math.min(18, containerHeight * 0.08)); // 自适应标题字体大小，范围10-18
      const axisLineWidth = Math.max(4, Math.min(12, containerHeight * 0.04)); // 自适应线条宽度，范围4-12

      const darkHud = this.props.visualTheme === 'dark';
      const gaugeTextColor = darkHud ? '#e8eef5' : '#4a4a4a';
      const gaugeTrackColor = darkHud ? 'rgba(255, 255, 255, 0.14)' : '#e0e0e0';
      const onDutyRate = this.getOnDutyRatePercent();

      const option = {
        grid: {
          left: '0%',
          right: '0%',
          top: '0%',
          bottom: '35%', // 增加底部空间，避免文字重叠
          containLabel: false
        },
        series: [
          {
            name: '在岗率',
            type: 'gauge',
            radius: '95%', // 从85%增加到95%，放大图表
            center: ['50%', '60%'], // 从55%调整到48%，图表稍微上移，给底部文字留空间
            startAngle: 180,
            endAngle: 0,
            min: 0,
            max: 100,
            splitNumber: 0,
            axisLine: {
              lineStyle: {
                width: axisLineWidth, // 自适应线条宽度
                color: [
                  [onDutyRate / 100, '#722ed1'], // 紫色填充部分
                  [1, gaugeTrackColor] // 背景部分（深色 HUD 用浅透明轨）
                ]
              }
            },
            axisTick: {
              show: false
            },
            splitLine: {
              show: false
            },
            axisLabel: {
              show: false
            },
            pointer: {
              show: false
            },
            detail: {
              show: true,
              offsetCenter: [0, '5%'], // 从15%调整到5%，百分比数字更靠近图表中心上方
              fontSize: detailFontSize, // 自适应字体大小
              fontWeight: 700,
              color: gaugeTextColor,
              formatter: `${onDutyRate}%`
            },
            data: [
              {
                value: onDutyRate,
                name: '在岗率',
                title: {
                  show: true,
                  offsetCenter: [0, '50%'], // 从30%调整到40%，"在岗率"文字更靠下，避免与百分比重叠
                  fontSize: titleFontSize, // 自适应标题字体大小
                  color: gaugeTextColor
                }
              }
            ]
          }
        ]
      };

      this.chartInstanceRef.current.setOption(option);
    }
  }

  // 处理窗口大小变化
  handleResize = () => {
    if (this.chartInstanceRef.current && this.gaugeChartRef.current) {
      // 重新计算自适应字体大小
      const containerHeight = this.gaugeChartRef.current.offsetHeight || 200;
      const detailFontSize = Math.max(14, Math.min(32, containerHeight * 0.12));
      const titleFontSize = Math.max(10, Math.min(18, containerHeight * 0.08));
      const axisLineWidth = Math.max(4, Math.min(12, containerHeight * 0.04));
      
      // 更新图表配置
      this.chartInstanceRef.current.setOption({
        series: [{
          axisLine: {
            lineStyle: {
              width: axisLineWidth
            }
          },
          detail: {
            fontSize: detailFontSize
          },
          data: [{
            title: {
              fontSize: titleFontSize
            }
          }]
        }]
      });
      
      this.chartInstanceRef.current.resize();
    }
  }

  componentDidMount() {
    this.initChart();
    window.addEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.commandCenterStaffAgentsPageObj !== this.props.commandCenterStaffAgentsPageObj ||
      prevProps.queryManager?.currentQuery?.store_id !== this.props.queryManager?.currentQuery?.store_id
    ) {
      this.initChart();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this.chartInstanceRef.current) {
      this.chartInstanceRef.current.dispose();
      this.chartInstanceRef.current = null;
    }
  }

  render() {
    const staffTotal = this.getDisplayStaffTotalRow();
    return (
      <div className="staff-management-module">
        <div className="staff-container">
          <div className="staff-content">
            {/* 左侧：统计数据 */}
            <div className="staff-stats">
              <div className="staff-header">
                <div className="staff-title">员工总数</div>
              </div>
              <div className="staff-total">{renderFormattedInt(staffTotal)}</div>
              <div className="staff-sub-stats">
                <div className="staff-stat-item">
                  <span className="staff-stat-label">在岗数: </span>
                  <span className="staff-stat-value staff-stat-value-purple">{renderFormattedInt(staffTotal)}</span>
                </div>
                <div className="staff-stat-item">
                  <span className="staff-stat-label">离岗: </span>
                  <span className="staff-stat-value staff-stat-value-red">{renderFormattedInt(0)}</span>
                </div>
              </div>
            </div>

            {/* 右侧：仪表盘 */}
            <div className="staff-gauge">
              <div ref={this.gaugeChartRef} className="staff-gauge-chart"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default StaffManagementModule;
